'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { RotateCcw, Scissors } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Avatar } from '@/components/common';
import { Button, Modal } from '@/components/ui';
import styles from './AccountSurface.module.css';
import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';

const AVATAR_OUTPUT_SIZE = 320;
const AVATAR_PREVIEW_SIZE = 160;
const AVATAR_OUTPUT_QUALITY = 0.8;

interface AvatarCropModalProps {
  isOpen: boolean;
  imageUrl: string | null;
  fileName: string;
  userId: string | null;
  displayName: string;
  email: string;
  onClose: () => void;
  onConfirm: (file: File, previewUrl: string) => Promise<void> | void;
}

function buildCenteredSquareCrop(width: number, height: number): Crop {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 80 }, 1, width, height),
    width,
    height,
  );
}

function cropToPixelCrop(crop: Crop, width: number, height: number): PixelCrop {
  if (crop.unit === '%') {
    return {
      unit: 'px',
      x: Math.round((crop.x / 100) * width),
      y: Math.round((crop.y / 100) * height),
      width: Math.round((crop.width / 100) * width),
      height: Math.round((crop.height / 100) * height),
    };
  }

  return {
    unit: 'px',
    x: Math.round(crop.x),
    y: Math.round(crop.y),
    width: Math.round(crop.width),
    height: Math.round(crop.height),
  };
}

async function renderAvatarBlob(
  image: HTMLImageElement,
  crop: PixelCrop,
  displayWidth: number,
  displayHeight: number,
  outputSize: number,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / displayWidth;
    const scaleY = image.naturalHeight / displayHeight;

    const pixelX = Math.round(crop.x * scaleX);
    const pixelY = Math.round(crop.y * scaleY);
    const pixelW = Math.max(1, Math.round(crop.width * scaleX));
    const pixelH = Math.max(1, Math.round(crop.height * scaleY));

    canvas.width = outputSize;
    canvas.height = outputSize;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas 2D context unavailable'));
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, pixelX, pixelY, pixelW, pixelH, 0, 0, outputSize, outputSize);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Avatar rendering failed'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}

function makeAvatarFileName(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '').trim() || 'avatar';
  return `${base}-avatar.jpg`;
}

export function AvatarCropModal({
  isOpen,
  imageUrl,
  fileName,
  userId,
  displayName,
  email,
  onClose,
  onConfirm,
}: AvatarCropModalProps) {
  const t = useTranslations('nav');
  const commonT = useTranslations('common');

  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageReady, setImageReady] = useState(false);
  const previewGeneration = useRef(0);
  const saveLock = useRef(false);
  const currentPreview = useRef<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const revokePreviewUrl = useCallback(() => {
    previewGeneration.current += 1;
    if (currentPreview.current) URL.revokeObjectURL(currentPreview.current);
    currentPreview.current = null;
    setPreviewUrl(null);
  }, []);

  const updatePreview = useCallback(async (nextCrop: PixelCrop | undefined) => {
    const img = imgRef.current;
    if (!img || !nextCrop || nextCrop.width <= 0 || nextCrop.height <= 0) {
      revokePreviewUrl();
      return;
    }

    const generation = ++previewGeneration.current;
    try {
      const blob = await renderAvatarBlob(img, nextCrop, img.width, img.height, AVATAR_PREVIEW_SIZE, 0.82);
      if (generation !== previewGeneration.current) return;
      const nextUrl = URL.createObjectURL(blob);
      if (currentPreview.current) URL.revokeObjectURL(currentPreview.current);
      currentPreview.current = nextUrl;
      setPreviewUrl(nextUrl);
    } catch (error) {
      if (generation === previewGeneration.current) {
        console.error('Failed to render avatar preview:', error);
        revokePreviewUrl();
      }
    }
  }, [revokePreviewUrl]);

  useEffect(() => {
    setCrop(undefined);
    setCompletedCrop(undefined);
    setError('');
    setImageReady(false);
    revokePreviewUrl();
    return () => {
      previewGeneration.current += 1;
      if (currentPreview.current) URL.revokeObjectURL(currentPreview.current);
      currentPreview.current = null;
    };
  }, [isOpen, imageUrl, revokePreviewUrl]);

  const handleImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setImageReady(true);
    setError('');
    const initialCrop = buildCenteredSquareCrop(img.width, img.height);
    const initialPixelCrop = cropToPixelCrop(initialCrop, img.width, img.height);
    setCrop(initialCrop);
    setCompletedCrop(initialPixelCrop);
    void updatePreview(initialPixelCrop);
  }, [updatePreview]);

  const handleReset = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;

    const initialCrop = buildCenteredSquareCrop(img.width, img.height);
    const initialPixelCrop = cropToPixelCrop(initialCrop, img.width, img.height);
    setCrop(initialCrop);
    setCompletedCrop(initialPixelCrop);
    void updatePreview(initialPixelCrop);
  }, [updatePreview]);

  const handleConfirm = useCallback(async () => {
    const img = imgRef.current;
    if (saveLock.current || !imageReady || !img || !completedCrop || completedCrop.width <= 0 || completedCrop.height <= 0) {
      return;
    }

    saveLock.current = true;
    setSaving(true);
    setError('');
    try {
      const blob = await renderAvatarBlob(
        img,
        completedCrop,
        img.width,
        img.height,
        AVATAR_OUTPUT_SIZE,
        AVATAR_OUTPUT_QUALITY,
      );
      const file = new File([blob], makeAvatarFileName(fileName), { type: 'image/jpeg' });
      const nextPreviewUrl = URL.createObjectURL(blob);

      try {
        await onConfirm(file, nextPreviewUrl);
      } catch (error) {
        URL.revokeObjectURL(nextPreviewUrl);
        throw error;
      }
    } catch {
      setError(t('avatarUploadFailed'));
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  }, [completedCrop, fileName, imageReady, onConfirm, t]);

  if (!isOpen || !imageUrl) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!saveLock.current) onClose(); }}
      title={t('avatarCropTitle')}
      subtitle={t('avatarCropHint')}
      size="xl"
      className={`${styles.crop} max-w-5xl`}
      persistent={saving}
    >
      <LiquidGlass className={styles.modalLens}><span /></LiquidGlass>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_240px]">
        <div className="overflow-hidden rounded-[12px] border border-hr-border-dim bg-[radial-gradient(circle_at_top,_rgba(15,122,199,0.12),_rgba(15,122,199,0.03)_42%,_transparent_75%)] p-4">
          <div className="flex min-h-[min(360px,42dvh)] items-center justify-center rounded-[10px] border border-dashed border-hr-border bg-white/80 p-4">
            <ReactCrop
              crop={crop}
              onChange={(nextCrop) => setCrop(nextCrop)}
              onComplete={(nextCrop) => {
                setCompletedCrop(nextCrop);
                void updatePreview(nextCrop);
              }}
              aspect={1}
              circularCrop
              keepSelection
              ruleOfThirds
            >
              <img
                ref={imgRef}
                src={imageUrl}
                alt={t('avatarCropImageAlt')}
                onLoad={handleImageLoad}
                onError={() => { setImageReady(false); setCompletedCrop(undefined); revokePreviewUrl(); setError(t('avatarPrepareFailed')); }}
                className="max-h-[60vh] w-auto max-w-full rounded-[10px] object-contain"
                draggable={false}
              />
            </ReactCrop>
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-[12px] border border-hr-border-dim bg-surface-hover/60 p-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-hr-text-tertiary">
              {t('avatarCropPreviewLabel')}
            </p>
            <p className="mt-2 text-sm leading-6 text-hr-text-secondary">
              {t('avatarCropPreviewHint')}
            </p>
          </div>

          <div className="rounded-[12px] border border-hr-border-dim bg-white px-5 py-6 shadow-subtle">
            <Avatar
              src={previewUrl}
              userId={userId}
              name={displayName}
              email={email}
              size={160}
              className="mx-auto ring-1 ring-black/6"
              textClassName="text-5xl"
            />
          </div>

          <div className="rounded-2xl border border-hr-border-dim bg-white/85 p-4 text-sm leading-6 text-hr-text-secondary">
            <div className="flex items-start gap-2">
              <Scissors className="mt-0.5 h-4 w-4 shrink-0 text-accent-core" />
              <p>{t('avatarCropTip')}</p>
            </div>
          </div>

          <div aria-live="polite">{error && <p role="alert" className="text-sm text-red-700">{error}</p>}</div>
          <div className="mt-auto flex gap-2">
            <Button
              variant="ghost"
              className="flex-1"
              icon={<RotateCcw className="h-4 w-4" />}
              onClick={handleReset}
              disabled={saving || !imageReady}
            >
              {t('avatarCropReset')}
            </Button>
            <Button
              className="flex-1"
              onClick={handleConfirm}
              isLoading={saving}
              disabled={saving || !imageReady || !completedCrop || completedCrop.width <= 0 || completedCrop.height <= 0}
            >
              {saving ? t('avatarCropSaving') : commonT('confirm')}
            </Button>
          </div>

          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {commonT('cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}